import React, { Component } from 'react';
import API from './API';
import { T } from './traks';

/* returns true if a and b are the same */
function ApifyDefaultEqualityComparator(a, b) {
	const ta = typeof a;
	const tb = typeof b;
	if (ta !== tb) return false;

	const t = ta;
	if (t === "function") return true; // assuming functions are always equal
	if (t !== "object") return a === b;
	if (a === null || b === null) return a === b;
	if (a.constructor !== b.constructor) return false;
	if (a.constructor !== Object && a.constructor !== Array) return a === b;

	var keys = {};
	for (const o of [a,b]) for (const key of Object.keys(o)) keys[key] = true;
	for (const key of Object.keys(keys)) {
		if (!ApifyDefaultEqualityComparator(a[key], b[key])) return false;
	}
	return true;
}

class ApifyAsyncConstructor {
	/* implement this; must return an ApifyAsync or an ApifyAsyncConstructor */
	construct_async_impl() {
		throw new Error("implement me");
	}

	construct_async() {
		var async = this.construct_async_impl();
		while (true) {
			if (async instanceof ApifyAsyncConstructor) {
				async = async.construct_async_impl();
				continue;
			} else if (async instanceof ApifyAsync) {
				break;
			} else {
				throw new Error("not an ApifyAsyncConstructor, nor an ApifyAsync: " + async);
			}
		}
		if (this.count_prop) async.set_count_prop(this.count_prop);
		return async;
	}

	/* passes emission count in props[name]; this increments every time new
	 * a new response is delivered to the wrapped component */
	with_count_prop(name = "count") {
		this.count_prop = name;
		return this;
	}

	wrap(MyComponent, LoadingComponent = DefaultLoadingComponent) {
		const construct_async = () => this.construct_async();
		return class extends Component {
			constructor(props) {
				super(props);
				this.state = {async_props: null, is_loaded: false};
				this.async = construct_async();
			}

			componentDidMount() {
				this.async.set_output_listener((output) => {
					this.setState({async_props: output, is_loaded: true});
				});
				this.async.set_input(this.props);
			}

			componentWillUnmount() {
				this.async.stop();
			}

			componentDidUpdate(prev_props, prev_state) {
				this.async.set_input(this.props);
			}

			render() {
				if (!this.state.is_loaded) {
					return <LoadingComponent/>;
				} else {
					return <MyComponent {...this.props} {...this.state.async_props}/>;
				}
			}
		}
	}
}

class _ApifyAsyncConstructorSimple extends ApifyAsyncConstructor {
	constructor(ctor) {
		super();
		this.ctor = ctor;
	}

	construct_async_impl() {
		return this.ctor();
	}
}

function ApifyAsyncConstructorSimple(ctor) {
	return new _ApifyAsyncConstructorSimple(ctor);
}

class ApifyAsync {
	constructor() {
		this.output_listener = null;
		this.emit_count = 0;
	}

	set_count_prop(count_prop) {
		this.count_prop = count_prop;
	}

	set_output_listener(listener) {
		if (this.output_listener !== null) throw new Error("set_output_listener called more than onceA");
		this.output_listener = listener;
	}

	emit(output) {
		if (this.output_listener === null) throw new Error("emit() called, but we have no output listener");
		if (typeof output === "object" && this.count_prop) {
			output = {...output, [this.count_prop]: this.emit_count++};
		}
		this.output_listener(output);
	}

	set_input(input) {
		throw new Error("implement me");
	}

	/* MUST ensure that no output listener is ever called again. SHOULD
	 * abort any in-flight asynchronous operations */
	stop() {
		throw new Error("implement me");
	}
}

class _ApifyGet extends ApifyAsync {
	constructor(a) {
		super();
		this.a = a;
		this.current_input = undefined;
		this.abort_fn = null;
		this.initial_input = true;
	}

	fetch() {
		this.abort();
		const query = this.a.query_from_props(this.current_input);
		if (query === null) {
			this.abort_fn = null;
			return;
		}

		var endpoint = this.a.endpoint;
		const path_params = this.a.path_params_from_props(this.current_input);
		if (Object.keys(path_params).length > 0) {
			endpoint = endpoint.split("/").map((f) => {
				if (f[0] === ":") {
					var val = path_params[f.slice(1)];
					if (val === undefined) throw new Error(f + " in endpoint " + this.a.endpoint + " was not found in path params: " + JSON.stringify(path_params));
					return val;
				} else {
					return f;
				}
			}).join("/");
		}

		const xhr = API.get(endpoint, query, (response, http_status, ttl_in_seconds) => {
			this.abort_fn = null;
			const hc = Math.floor(http_status / 100);
			var output;
			if (hc === 0) {
				return;
			} else if (hc === 2) { // 2xx
				output = this.a.props_from_response(response);
			} else if (hc === 4 && this.a.error_4xx_prop !== null) { // 4xx
				output = {[this.a.error_4xx_prop]: {response, http_status}};
			} else {
				throw new Error(`bad HTTP response; status=${http_status}; endpoint=${this.a.endpoint}; query=${JSON.stringify(query)}; response=${JSON.stringify(response)}`);
			}

			const reload = () => { this.fetch(); }

			const timeout = (seconds) => {
				this.abort();
				if (seconds > 0) {
					const th = setTimeout(() => {
						this.abort_fn = null;
						reload();
					}, seconds*1000);
					this.abort_fn = () => { clearTimeout(th); }
				}
			};

			if (this.a.use_api_refresh && ttl_in_seconds !== undefined) {
				timeout(ttl_in_seconds);
			} else if (this.a.refresh_interval) {
				timeout(this.a.refresh_interval);
			}

			const ctl = {reload, timeout}; // TODO stop()? something that stops refreshing indefinitely (not the same as abortable.abort())

			if (this.a.ctl_prop) output[this.a.ctl_prop] = ctl;
			if (this.a.response_handler) this.a.response_handler(response, http_status, ctl); // XXX?

			this.emit(output);
		});
		this.abort_fn = () => { xhr.abort(); };
	}

	set_input(input) {
		if (this.initial_input || !this.a.prop_comparator(this.current_input, input)) {
			this.current_input = input;
			this.initial_input = false;
			if (!this.a.predicate || this.a.predicate(input)) {
				this.fetch();
			} else {
				this.emit();
			}
		}
	}

	abort() {
		if (this.abort_fn) {
			this.abort_fn();
			this.abort_fn = null;
		}
	}

	stop() {
		this.abort();
		this.a = null;
	}
}

class _ApifyMany extends ApifyAsync {
	constructor(ctors) {
		super();
		this.asyncs = ctors.map(ctor => ctor.construct_async());
	}

	stop() {
		for (const async of this.asyncs) async.stop();
		this.asyncs = null;
	}
}

class _ApifyParallel extends _ApifyMany {
	constructor(ctors, reset_on_set_input) {
		super(ctors);
		this.reset_on_set_input = reset_on_set_input;

		this.reset();

		const setup_output_listener = (index) => {
			this.asyncs[index].set_output_listener((output) => {
				this.loaded[index] = true;
				const n_loaded = this.loaded.filter(x=>x).length;
				this.outputs[index] = output;
				if (n_loaded === this.asyncs.length) {
					var emission = {};
					for (const output of this.outputs) {
						emission = {...emission, ...output};
					}
					this.emit(emission);
				}
			});
		};

		for (var i = 0; i < this.asyncs.length; i++) setup_output_listener(i);
	}

	reset() {
		this.loaded = [];
		this.outputs = [];
		for (var i = 0; i < this.asyncs.length; i++) {
			this.loaded.push(false);
			this.outputs.push(null);
		}
	}

	set_input(input) {
		if (this.reset_on_set_input) this.reset();
		for (var i = 0; i < this.asyncs.length; i++) this.asyncs[i].set_input(input);
	}
}

class _ApifySerial extends _ApifyMany {
	constructor(ctors, do_accumulate) {
		super(ctors);

		const n_asyncs = this.asyncs.length;

		if (do_accumulate) {
			this.accumulated_output = {};
		}

		const setup_daisy_chain_output_listener = (i) => {
			this.asyncs[i].set_output_listener((output) => {
				if (do_accumulate) {
					this.accumulated_output = {...this.accumulated_output, ...output};
				}
				this.asyncs[i+1].set_input(output);
			});
		};

		for (var i = 0; i < (n_asyncs-1); i++) {
			setup_daisy_chain_output_listener(i);
		}

		this.asyncs[n_asyncs-1].set_output_listener((output) => {
			if (do_accumulate) {
				this.emit({...this.accumulated_output, ...output});
			} else {
				this.emit(output);
			}
		});
	}

	set_input(input) {
		if (this.asyncs.length > 0) this.asyncs[0].set_input(input);
	}
}

class _ApifyMap extends ApifyAsync {
	constructor(mapper) {
		super();
		this.mapper = mapper;
	}

	set_input(input) {
		this.emit(this.mapper(input));
	}

	stop() {
		this.mapper = null;
	}
}

class _Apify extends ApifyAsyncConstructor {
	constructor(endpoint, query) {
		super();
		this.endpoint = endpoint;
		this.loading_prop = null;
		this.ctl_prop = null;
		this.error_4xx_prop = null;
		this.refresh_interval = null;
		this.use_api_refresh = false;
		this.response_handler = null;
		this.with_query_from_props((props) => (query || {}));
		this.with_path_params_from_props((props) => ({}));
		this.with_response_prop("response");
		this.with_query_comparator(ApifyDefaultEqualityComparator);
	}

	/* Pass a function that maps props to a query for the API endpoint.
	 * this overrides the query passed to the constructor, if any. The
	 * passed function is called immediately before an API call is done,
	 * but per default it's also used for update dectection (see
	 * with_query_comparator()) so the function SHOULD NOT have any
	 * side-effects */
	with_query_from_props(mapper) {
		this.query_from_props = mapper;
		return this;
	}

	/* Similar to with_query_from_props(), but injects variables into
	 * paths, e.g. {id:42} with the path /resource/:id/sub yields
	 * /resource/42/sub */
	with_path_params_from_props(mapper) {
		this.path_params_from_props = mapper;
		return this;
	}

	/* Pass a function that returns true only if the Apify should load its
	 * resource. If it returns false, the Apify will immediately finish,
	 * yielding no response */
	with_predicate(mapper) {
		this.predicate = mapper;
		return this;
	}

	/* This function is called exactly once, immediately after a
	 * non-throwing API response is received. Use it to implement
	 * statefulness, e.g. to change the query from props mapper return
	 * value after each received API response. The passed function is called like:
	 *   handler(response, http_status, ctl)
	 * response: API response
	 * http_status: HTTP status code
	 * ctl: see with_ctl_prop()
	 **/
	with_response_handler(handler) {
		this.response_handler = handler;
		return this;
	}

	/* Pass a function that maps the response to a props object. The passed
	 * function SHOULD NOT have any side-effects (to update an internal
	 * state after receiving a response, use with_response_handler()) */
	with_props_from_response(mapper) {
		this.props_from_response = mapper;
		return this;
	}

	/* Alternative to with_props_from_response: passes the entire response
	 * in props[name] */
	with_response_prop(name) {
		this.with_props_from_response((response) => ({[name]: response}));
		return this;
	}

	/* reloads periodically from API at the specified interval */
	with_refresh_interval(seconds) {
		this.refresh_interval = seconds;
		this.use_api_refresh = false;
		return this;
	}

	/* reloads periodically from API, using HTTP cache headers to calculate
	 * time-to-live */
	with_api_refresh() {
		this.use_api_refresh = true;
		this.refresh_interval = null;
		return this;
	}

	/* Disables loading component behavior, and instead renders the wrapped
	 * component immediately, passing loading status in the specified prop
	 * (true while loading, false when loaded). However, when used with
	 * ApifyMany(), rendering will be deferred until only Apify's
	 * configured with_loading_prop remain to be loaded. Also, note that
	 * .with_loading_prop() doesn't affect dependency-behavior; if
	 * A.depends_on(B), then B won't be loaded until A is loaded,
	 * regardless of whether A has been configured with
	 * .with_loading_prop() or not */
	with_loading_prop(name = "is_loading") {
		this.loading_prop = name;
		this.loading_component = null;
		return this;
	}

	/* passes an object in props[name] with two functions:
	 *  - .reload(): immediately reloads data from API
	 *  - .timeout(seconds): reloads data from API after the specified
	 *  seconds have elapsed. Be careful when using this together with
	 *  with_refresh_interval() and with_api_refresh(); calling timeout()
	 *  immediately aborts any in-flight API calls, or timeouts, but after
	 *  an API response is received, behavior is back to normal, i.e.
	 *  reloading resumes like it was configured using
	 *  with_refresh_interval() or with_api_refresh() */
	with_ctl_prop(name = "ctl") {
		this.ctl_prop = name;
		return this;
	}

	/* normally, a 4xx response causes an error to be thrown, but by using
	 * this function, you'll instead receive an {response, http_status}
	 * object in the specified prop */
	with_4xx_prop(name = "error") {
		this.error_4xx_prop = name;
		return this;
	}

	with_prop_comparator(comparator) {
		this.prop_comparator = comparator;
		return this;
	}

	with_query_comparator(comparator) {
		return this.with_prop_comparator(
			(a,b) => comparator(this.query_from_props(a), this.query_from_props(b))
		);
	}

	construct_async_impl() {
		return new _ApifyGet(this);
	}
}
function Apify(endpoint, query) { return new _Apify(endpoint, query); }

function flatten_ctor_args(args) {
	var xs = [];
	for (const arg of args) {
		if (arg === undefined || arg === null) {
			continue;
		} else if (arg instanceof Array) {
			xs = xs.concat(flatten_ctor_args(arg));
		} else if (arg instanceof ApifyAsyncConstructor) {
			xs.push(arg);
		} else {
			throw new Error("not an array, nor an ApifyAsyncConstructor: " + arg);
		}
	}
	return xs;
}

function ApifyParallel() {
	const argv = flatten_ctor_args(arguments);
	if (argv.length === 1) return argv[0];
	return ApifyAsyncConstructorSimple(() => new _ApifyParallel(argv, true));
}

function ApifyParallelNoReset() {
	const argv = flatten_ctor_args(arguments);
	if (argv.length === 1) return argv[0];
	return ApifyAsyncConstructorSimple(() => new _ApifyParallel(argv, false));
}

function ApifySerial() {
	const argv = flatten_ctor_args(arguments);
	if (argv.length === 1) return argv[0];
	return ApifyAsyncConstructorSimple(() => new _ApifySerial(argv));
}

function ApifySerialAccumulate() {
	const argv = flatten_ctor_args(arguments);
	if (argv.length === 1) return argv[0];
	return ApifyAsyncConstructorSimple(() => new _ApifySerial(argv, true));
}

function ApifyMap(mapper) {
	return ApifyAsyncConstructorSimple(() => new _ApifyMap(mapper));
}

function DefaultLoadingComponent(props) {
	return <div><T>Loading...</T></div>;
}

function ApifyFullScreenLoadingComponent(props) {
	return <div className="ad_fullscreen_padding">Loading...</div>;
}

class _ImportifyAsync extends ApifyAsync {
	constructor(a) {
		super();
		this.a = a;
		this.is_stopped = false;
	}

	set_input(input) {
		if (this.is_stopped) return;
		var do_import = true;
		if (this.a.predicate) do_import = this.a.predicate(input);

		if (do_import) {
			const module = this.a.module_from_props(input);
			if (module === this.loaded_module) return;
			this.loaded_module = module;
			var p = import(`./dynamic/${module}`).then(module => {
				if (this.is_stopped) return;
				this.emit({[this.a.module_prop]: module});
			});
			if (this.a.fail_mapper) {
				/*p = */p.catch(err => {
					if (this.is_stopped) return;
					this.emit({[this.a.module_prop]: this.a.fail_mapper(err)});
				});
			}
		} else {
			if (!this.loaded_module) {
				this.emit({[this.a.module_prop]: null});
				this.loaded_module = true;
			}
		}
	}

	stop() {
		this.is_stopped = true;
	}
}

/* Importify is used for Apify-compatible dynamic module imports.
 *
 * Dynamic modules must reside in `reaquity/src/dynamic/`; this relates to how
 * dynamic imports work in WebPack (it compiles _everything_ below
 * `reaquity/src/dynamic/` as dynamically importable modules). Also, module
 * paths are relative to this, e.g. the module "Foo" will actually be loaded as
 * "./dynamic/Foo".
 *
 * Use either a static module name (as the argument passed to Importify()), or
 * more interestingly; use with_module_from_props() (which is similar to
 * with_query_from_props()). So say you have a "style id" in props.style_id,
 * you might do something like:
 *    Importify().with_module_from_props(props => "styles/StyleFor" + props.style_id)...
 * and if props.style_id is "Blubber" it will then dynamically load
 * ./dynamic/styles/StyleForBlubber
 *
 * The module is passed in `props.module` by default, but can be overriden with
 * .with_module_prop()
 */
class _Importify extends ApifyAsyncConstructor {
	constructor(static_module) {
		super();
		this.with_module_from_props((props) => static_module);
		this.with_module_prop("module");
	}

	with_predicate(mapper) {
		this.predicate = mapper;
		return this;
	}

	with_module_from_props(mapper) {
		this.module_from_props = mapper;
		return this;
	}

	with_module_prop(name) {
		this.module_prop = name;
		return this;
	}

	with_fail_mapper(mapper) {
		this.fail_mapper = mapper;
		return this;
	}

	construct_async_impl() {
		return new _ImportifyAsync(this);
	}
}

function Importify(static_module) { return new _Importify(static_module); }

export {
	Apify,
	ApifySerial,
	ApifySerialAccumulate,
	ApifyParallel,
	ApifyParallelNoReset,
	ApifyMap,

	/* for making new async operations */
	ApifyAsync,
	ApifyAsyncConstructor,
	ApifyAsyncConstructorSimple,

	/* in case you want to base an Apify() comparator on the default one */
	ApifyDefaultEqualityComparator,

	ApifyFullScreenLoadingComponent,

	Importify,
};



// WEBPACK FOOTER //
// ./src/Apify.js